import { memo, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Warning as WarningIcon } from '@mui/icons-material';

interface TerminateConfirmProps {
  stackName: string;
  accountId: string;
  confirmToken: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const TerminateConfirm = memo<TerminateConfirmProps>(({
  stackName,
  accountId,
  confirmToken,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [typed, setTyped] = useState('');
  const matched = typed.trim() === confirmToken;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
        borderRadius: 1,
        p: 1.5,
        mt: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main', fontSize: 13, fontWeight: 500, mb: 0.75 }}>
        <WarningIcon sx={{ fontSize: 14 }} /> Terminate deployment
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.5 }}>
        Sets desired status to <strong>terminated</strong>. The reconciler tears down{' '}
        <Box component="strong" sx={{ fontFamily: 'monospace' }}>{stackName}</Box> in account{' '}
        <strong>{accountId}</strong>. The OpsPilot record is retained for audit until teardown confirms.{' '}
        Type <Box component="strong" sx={{ fontFamily: 'monospace' }}>{confirmToken}</Box> to enable.
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder={confirmToken}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={submitting}
        slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25 }}>
        <Button size="small" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={!matched || submitting}
          onClick={onConfirm}
        >
          Terminate
        </Button>
      </Box>
    </Box>
  );
});

TerminateConfirm.displayName = 'TerminateConfirm';
export default TerminateConfirm;
